import { useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CalendarIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { cn } from '../cn';
import { Button } from './primitives';

/**
 * How far ahead the year dropdown reaches when the caller sets no maximum.
 * Long enough for a multi-year agreement, short enough to stay a short list.
 */
const DEFAULT_YEARS_AHEAD = 5;

/** How a picked date is written on the trigger. */
const DISPLAY_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
};

/**
 * Parses the `yyyy-mm-dd` string a form holds into a local-midnight Date.
 *
 * The `T00:00:00` matters: `new Date('2026-10-15')` is parsed as UTC and lands
 * on the 14th for anyone west of Greenwich.
 *
 * @param value - A `yyyy-mm-dd` string, or '' when unset.
 * @returns The date, or undefined when unset or unparseable.
 */
function parseValue(value: string | undefined): Date | undefined {
  if (value === undefined || value === '') return undefined;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/**
 * Formats a Date back to the `yyyy-mm-dd` a form holds.
 *
 * @param date - The picked date.
 * @returns The ISO calendar date, in the viewer's own timezone.
 */
function formatValue(date: Date): string {
  const pad = (part: number): string => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Calendar classes, in portal tokens rather than shadcn's defaults. */
const CALENDAR_CLASSES = {
  months: 'relative',
  month: 'w-full',
  month_caption: 'flex h-9 items-center justify-center px-9',
  dropdowns: 'flex items-center gap-2',
  // Each dropdown is a native <select> laid invisibly over the label span
  // beneath it: the select keeps the platform's own picker and keyboard
  // behaviour, the span is what anyone actually sees.
  dropdown_root: 'relative inline-flex items-center',
  dropdown: 'absolute inset-0 z-10 size-full cursor-pointer opacity-0',
  caption_label: 'inline-flex items-center gap-0.5 text-portal-small font-bold',
  nav: 'absolute inset-x-0 top-0 flex h-9 items-center justify-between',
  button_previous:
    'inline-flex size-7 items-center justify-center rounded-portal-button text-portal-muted hover:bg-portal-sky hover:text-portal-ink disabled:opacity-40',
  button_next:
    'inline-flex size-7 items-center justify-center rounded-portal-button text-portal-muted hover:bg-portal-sky hover:text-portal-ink disabled:opacity-40',
  month_grid: 'w-full border-collapse',
  weekdays: 'flex',
  weekday: 'w-9 text-portal-micro font-normal text-portal-muted',
  week: 'mt-0.5 flex w-full',
  day: 'size-9 p-0 text-center',
  day_button:
    'size-9 rounded-portal-button text-portal-small hover:bg-portal-sky aria-selected:bg-brand aria-selected:font-bold aria-selected:text-white',
  today: 'font-bold text-brand',
  outside: 'text-portal-muted opacity-50',
  disabled: 'text-portal-muted opacity-40',
  hidden: 'invisible',
} as const;

/**
 * The trigger's surface. `input` matches {@link TextInput} so a date sits in a
 * form beside text fields; `button` is the standalone secondary button, for a
 * date that acts as a control rather than a field.
 */
export type DatePickerVariant = 'input' | 'button';

/** The `input` trigger's surface — deliberately identical to {@link TextInput}. */
const INPUT_TRIGGER =
  'mt-1.5 flex h-[43px] w-full items-center gap-2 rounded-portal-input border border-[#cad3df] bg-white px-3 text-left text-portal-small font-normal';

export interface DatePickerProps {
  readonly id?: string;
  /** `yyyy-mm-dd`; '' or undefined when unset. */
  readonly value: string | undefined;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly variant?: DatePickerVariant;
  /** Earliest selectable date, as `yyyy-mm-dd`. */
  readonly min?: string;
  /** Latest selectable date, as `yyyy-mm-dd`. */
  readonly max?: string;
  readonly disabled?: boolean;
  readonly className?: string;
  /** Labels the trigger when no visible `<label>` points at it. */
  readonly 'aria-label'?: string;
}

/**
 * The date picker every date field in every BCE app uses.
 *
 * One component rather than a native `<input type="date">`: the native control
 * renders differently in every browser, cannot be styled to the portal, and
 * shows `mm/dd/yyyy` to people who do not write dates that way.
 *
 * Value travels as `yyyy-mm-dd` so form state stays plain strings.
 *
 * @param props - Value, change handler and optional bounds.
 * @returns The trigger button and its calendar popover.
 */
export function DatePicker({
  id,
  value,
  onChange,
  placeholder = 'Pick a date',
  variant = 'input',
  min,
  max,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: DatePickerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const selected = parseValue(value);
  // react-day-picker reads a range the other way round: "before X" is the
  // disabled side of a minimum, "after Y" the disabled side of a maximum.
  const earliest = min === undefined ? undefined : parseValue(min);
  const latest = max === undefined ? undefined : parseValue(max);
  // Each bound is its own matcher: react-day-picker's `DateBefore` and
  // `DateAfter` are exact shapes, so one object carrying both matches neither.
  const bounds = [
    ...(earliest ? [{ before: earliest }] : []),
    ...(latest ? [{ after: latest }] : []),
  ];

  // How far the calendar can be navigated, which is separate from which days
  // are selectable. With a year dropdown and no explicit end, react-day-picker
  // stops at the end of the current year — so in December a booking could not
  // be dated into January, and a credit expiry could never be extended, since
  // an extension is by definition a date after the one it replaces.
  //
  // A bound given by the caller wins. Otherwise the window is wide enough for
  // the scheduling this product does without offering a scroll through a
  // century of years.
  const today = new Date();
  const navStart = earliest ?? new Date(today.getFullYear() - 1, 0, 1);
  const navEnd = latest ?? new Date(today.getFullYear() + DEFAULT_YEARS_AHEAD, 11, 31);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        {variant === 'input' ? (
          <button
            id={id}
            type="button"
            disabled={disabled}
            aria-label={ariaLabel}
            className={cn(INPUT_TRIGGER, disabled && 'opacity-60', className)}
          >
            <CalendarIcon aria-hidden className="size-4 shrink-0 text-portal-muted" />
            <span className={selected ? undefined : 'text-portal-muted'}>
              {selected ? selected.toLocaleDateString('en-US', DISPLAY_FORMAT) : placeholder}
            </span>
          </button>
        ) : (
          <Button
            id={id}
            type="button"
            variant="secondary"
            disabled={disabled}
            aria-label={ariaLabel}
            className={cn('w-full justify-start gap-2 font-normal', className)}
          >
            <CalendarIcon aria-hidden className="size-4 shrink-0 text-portal-muted" />
            <span className={selected ? undefined : 'text-portal-muted'}>
              {selected ? selected.toLocaleDateString('en-US', DISPLAY_FORMAT) : placeholder}
            </span>
          </Button>
        )}
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className="z-50 rounded-portal-card border border-portal-line bg-white p-3 shadow-portal-raised"
        >
          <DayPicker
            mode="single"
            captionLayout="dropdown"
            startMonth={navStart}
            endMonth={navEnd}
            showOutsideDays
            classNames={CALENDAR_CLASSES}
            components={{
              Chevron: ({ orientation }) => {
                // 'down' is the month and year dropdowns' own arrow. Without a
                // case for it, it fell through to the next-month chevron and
                // drew a stray '>' beside each caption.
                if (orientation === 'down') {
                  return <ChevronDownIcon aria-hidden className="size-3.5 text-portal-muted" />;
                }
                return orientation === 'left' ? (
                  <ChevronLeftIcon aria-hidden className="size-4" />
                ) : (
                  <ChevronRightIcon aria-hidden className="size-4" />
                );
              },
            }}
            {...(selected ? { selected, defaultMonth: selected } : {})}
            {...(bounds.length > 0 ? { disabled: bounds } : {})}
            onSelect={(date) => {
              onChange(date ? formatValue(date) : '');
              setOpen(false);
            }}
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
